import { useEffect, useRef } from "react";
import {
  Animated,
  Image,
  Linking,
  Platform,
  ScrollView,
  Text,
  View,
} from "react-native";
import {
  Globe,
  ListChecks,
  Phone,
  Pill,
  Receipt,
  Snowflake,
  Truck,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  MOCK_PHARMACY_DIRECTORY,
  MOCK_PHARMACY_MEDICATIONS,
  usePharmacy,
  type PharmacyDirectoryType,
} from "@src/packages/providers";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import { useLocale, useTranslations } from "@src/packages/i18n";
import { parseDateOrTimestamp } from "@src/packages/utils/date";
import type { AccessStackParamList } from "../../types";
import {
  ActionBanner,
  CardSurface,
  ClinicalDataStatus,
  CloseButton,
  DisclaimerNote,
  IconButton,
  MetaText,
  EmptyState,
  PrimaryButton,
  PrimaryTitleText,
  Row,
  SectionTitleRow,
  type SectionTitleRowAction,
  SupportingSubtitleText,
  Tag,
  semanticColors,
  LoadingState,
} from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import {
  MedicationRow,
  type MedicationForm,
} from "@src/packages/homepage/plan/components/MedicationRow";
import { pharmacyLogoImages } from "./pharmacyLogos";
import { useSmartBack } from "../../hooks/useSmartBack";

type Props = NativeStackScreenProps<AccessStackParamList, "PharmacyDetail">;

const TYPE_LABEL_KEYS: Record<PharmacyDirectoryType, string> = {
  specialty: "access.pharmacy.type.specialty",
  mail_order: "access.pharmacy.type.mail_order",
  retail: "access.pharmacy.type.retail",
};

const SPECIALTY_LABELS: Record<string, string> = {
  "trumprx-participating": "TrumpRx",
};

function formatSpecialty(value: string) {
  if (SPECIALTY_LABELS[value]) return SPECIALTY_LABELS[value];

  // Slugs read as sentence case; a compound such as "same-day" keeps its
  // hyphen, so only the last hyphen becomes a space.
  const words = value.replace(/-([^-]*)$/, " $1");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Match a patient prescription against a pharmacy's published price list.
 * Brand match is required; strength and dose-unit narrow when the brand
 * has multiple SKUs (e.g., Gonal-F 300 vs 450 vs 900 IU).
 */
function matchPublishedPrice(
  patientName: string | undefined,
  patientDose: string,
  patientDoseUnit: string | undefined,
  items: { medication: string; price: number }[],
) {
  if (!patientName) return undefined;
  const brandToken = patientName.trim().toLowerCase().split(/[\s(]/)[0];
  if (!brandToken) return undefined;
  const brandMatches = items.filter((item) =>
    item.medication.toLowerCase().includes(brandToken),
  );
  if (brandMatches.length === 0) return undefined;
  if (brandMatches.length === 1) return brandMatches[0];
  const doseNarrowed = patientDose
    ? brandMatches.filter((item) => item.medication.includes(patientDose))
    : brandMatches;
  if (doseNarrowed.length === 1) return doseNarrowed[0];
  const unitNarrowed =
    patientDoseUnit && doseNarrowed.length > 1
      ? doseNarrowed.filter((item) =>
          item.medication.toLowerCase().includes(patientDoseUnit.toLowerCase()),
        )
      : doseNarrowed;
  return unitNarrowed[0] ?? doseNarrowed[0] ?? brandMatches[0];
}

function getDemoMedicationPrice(
  pharmacy: {
    demo?: {
      medicationPrices?: { medicationId: string; price: number | null }[];
    };
  },
  medicationId: string,
) {
  return (
    pharmacy.demo?.medicationPrices?.find(
      (entry) => entry.medicationId === medicationId,
    )?.price ?? null
  );
}

function SectionHeading({
  title,
  icon,
  body,
  action,
}: {
  title: string;
  icon: LucideIcon;
  body?: string;
  action?: SectionTitleRowAction;
}) {
  return (
    <View style={{ gap: 2 }}>
      <SectionTitleRow
        title={title}
        icon={icon}
        action={action}
        style={{ marginBottom: 0, paddingHorizontal: 0 }}
      />
      {body ? <SupportingSubtitleText>{body}</SupportingSubtitleText> : null}
    </View>
  );
}

function FactLine({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 10 }}>
      <Icon
        size={15}
        color={semanticColors.typography.supporting}
        strokeWidth={1.6}
        style={{ marginTop: 1 }}
      />
      <SupportingSubtitleText style={{ flex: 1, marginTop: 0 }}>
        {label}
      </SupportingSubtitleText>
    </View>
  );
}

// The form a price-list line names, for the pen, vial or pill image the
// shared medication row draws.
function medicationFormFromName(name: string): MedicationForm {
  const lower = name.toLowerCase();
  if (/\bpen\b/.test(lower)) return "pen";
  if (/\b(vial|kit|pfs|syringe|ampoule)\b/.test(lower)) return "vial";
  return "pill";
}

function formatPrice(price: number) {
  return `$${price.toLocaleString("en-US", {
    minimumFractionDigits: price % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function PharmacyDetailScreen({ route }: Props) {
  const t = useTranslations();
  const { locale } = useLocale();
  const { pharmacyId, demo, returnTab } = route.params;
  const demoMode =
    demo === true ||
    (Platform.OS === "web" &&
      typeof window !== "undefined" &&
      ["1", "true"].includes(
        new URLSearchParams(window.location.search).get("demo") ?? "",
      ));
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Access",
    fallbackScreen: "Pharmacy",
  });
  const {
    data: pharmacy,
    source,
    isLoading,
    loadError,
    isFetching,
    refetch,
  } = usePharmacy(pharmacyId, {
    demo: demoMode,
  });
  const { data: patientMedications = [] } = usePatientMedications();
  const scale = useRef(new Animated.Value(0.96)).current;
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.spring(scale, {
        toValue: 1,
        friction: 8,
        tension: 65,
        useNativeDriver: true,
      }),
      Animated.timing(opacity, {
        toValue: 1,
        duration: 250,
        useNativeDriver: true,
      }),
    ]).start();
  }, [opacity, scale]);

  const openWebsite = () => {
    if (pharmacy?.website) void Linking.openURL(pharmacy.website);
  };

  const callPharmacy = () => {
    if (pharmacy?.phone) {
      void Linking.openURL(`tel:${pharmacy.phone.replace(/[^\d+]/g, "")}`);
    }
  };

  const openQuoteSource = () => {
    const url = pharmacy?.quoteUrl ?? pharmacy?.sourceUrl ?? pharmacy?.website;
    if (url) void Linking.openURL(url);
  };

  const medicationSummary = patientMedications
    .filter(
      (medication) =>
        medication.medication?.name || medication.rawMedicationName,
    )
    .map((medication, index) => {
      const name = medication.medication?.name ?? medication.rawMedicationName;
      const doseValue = medication.dose ? String(medication.dose) : "";
      const doseLabel = medication.dose
        ? `${medication.dose} ${medication.doseUnit ?? ""}`.trim()
        : "";
      const label = [name, doseLabel].filter(Boolean).join(" · ");
      const matchedPrice = pharmacy?.publishedPrices
        ? matchPublishedPrice(
            name ?? undefined,
            doseValue,
            medication.doseUnit ?? undefined,
            pharmacy.publishedPrices.items,
          )
        : undefined;
      return {
        key: `${medication.id}-${index}`,
        label,
        matchedPrice,
      };
    });

  // Dedupe by matched Mandell's SKU — a patient may log the same drug many
  // times in their calendar; only show one row per unique SKU.
  const matchedMedications = (() => {
    const seen = new Set<string>();
    const rows: (typeof medicationSummary)[number][] = [];
    for (const m of medicationSummary) {
      if (!m.matchedPrice) continue;
      const key = m.matchedPrice.medication;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push(m);
    }
    return rows;
  })();
  const publishedPriceTotal = matchedMedications.reduce(
    (sum, m) => sum + (m.matchedPrice?.price ?? 0),
    0,
  );

  const medicationPriceRows =
    source === "demo" && pharmacy
      ? MOCK_PHARMACY_MEDICATIONS.map((medication) => {
          const currentPrice = getDemoMedicationPrice(pharmacy, medication.id);
          const lowerElsewhere =
            currentPrice == null
              ? undefined
              : MOCK_PHARMACY_DIRECTORY.filter(
                  (candidate) => candidate.id !== pharmacy.id,
                )
                  .map((candidate) => ({
                    pharmacy: candidate,
                    price: getDemoMedicationPrice(candidate, medication.id),
                  }))
                  .filter(
                    (alternative) =>
                      alternative.price != null &&
                      alternative.price < currentPrice,
                  )
                  .sort((a, b) => (a.price ?? 0) - (b.price ?? 0))[0];

          return { currentPrice, lowerElsewhere, medication };
        })
      : [];
  const lowerPriceCount = medicationPriceRows.filter(
    (row) => row.lowerElsewhere,
  ).length;

  const contactLine = pharmacy
    ? [
        [pharmacy.city, pharmacy.state, pharmacy.country]
          .filter(Boolean)
          .join(", "),
        pharmacy.phone ?? t("access.pharmacy.phone_unverified"),
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  const delivery = pharmacy?.delivery;
  const deliveryFacts: { icon: LucideIcon; label: string }[] = [
    ...(delivery?.overnightNationwide
      ? [
          {
            icon: Truck,
            label: t("access.pharmacy.delivery_overnight_nationwide"),
          },
        ]
      : []),
    ...((delivery?.sameDayCities?.length ?? 0) > 0
      ? [
          {
            icon: Truck,
            label: t("access.pharmacy.delivery_same_day", {
              params: { cities: delivery!.sameDayCities!.join(", ") },
            }),
          },
        ]
      : []),
    ...(delivery?.coldChain
      ? [{ icon: Snowflake, label: t("access.pharmacy.delivery_cold_chain") }]
      : []),
    ...(delivery?.notes ? [{ icon: Truck, label: delivery.notes }] : []),
  ];

  const priceListUrl = pharmacy?.publishedPrices?.sourceUrl;
  const viewAllPrices: SectionTitleRowAction | undefined = priceListUrl
    ? {
        label: t("access.pharmacy.view_all_prices"),
        accessibilityLabel: t("access.pharmacy.published_prices_view_source"),
        onPress: () => void Linking.openURL(priceListUrl),
      }
    : undefined;

  return (
    <Animated.View
      style={{
        flex: 1,
        opacity,
        transform: [{ scale }],
      }}
    >
      <GlassScreenLayout>
        <ScrollView
          contentContainerStyle={{
            width: "100%",
            maxWidth: 520,
            alignSelf: "center",
            paddingHorizontal: 16,
            paddingTop: SCREEN_PADDING_TOP,
            paddingBottom: 100,
          }}
          showsVerticalScrollIndicator={false}
        >
          <CloseButton
            onPress={handleBack}
            accessibilityLabel={t("access.back")}
            style={{ alignSelf: "flex-end", marginBottom: 12 }}
          />

          {isLoading ? (
            <LoadingState
              caption={t("access.pharmacy.directory_loading_title")}
            />
          ) : loadError ? (
            <ClinicalDataStatus
              state="unavailable"
              title={t("access.pharmacy.directory_error_title")}
              message={t("states.check_connection_and_try_again")}
              retryLabel={t("clinical_data.retry")}
              retryingLabel={t("clinical_data.retrying")}
              onRetry={refetch}
              isRetrying={isFetching}
              testID="pharmacy-detail-unavailable"
            />
          ) : !pharmacy ? (
            <EmptyState
              title={t("access.pharmacy.pharmacy_not_in_directory")}
              testID="pharmacy-detail-not-found"
            />
          ) : (
            <CardSurface
              style={{
                gap: 24,
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              <View style={{ gap: 8 }}>
                {pharmacyLogoImages[pharmacy.id] ? (
                  <Image
                    source={pharmacyLogoImages[pharmacy.id]}
                    resizeMode="contain"
                    accessibilityIgnoresInvertColors
                    style={{ width: 116, height: 40, marginBottom: 4 }}
                  />
                ) : null}
                <Tag
                  label={t(TYPE_LABEL_KEYS[pharmacy.type])}
                  style={{ alignSelf: "flex-start" }}
                />
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 12,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      flex: 1,
                      color: semanticColors.text.primary,
                      fontSize: 28,
                      fontWeight: "700",
                      letterSpacing: -0.5,
                    }}
                  >
                    {pharmacy.name}
                  </Text>
                  {pharmacy.phone || pharmacy.website ? (
                    <View
                      style={{ flexDirection: "row", gap: 8, marginTop: 2 }}
                    >
                      {pharmacy.phone ? (
                        <IconButton
                          accessibilityLabel={`${t("access.pharmacy.call_pharmacy")} ${pharmacy.phone}`}
                          action="access.pharmacy.call_pharmacy"
                          onPress={callPharmacy}
                          hitSlop={6}
                          size={36}
                          surface="frosted"
                        >
                          <Phone
                            size={16}
                            color={semanticColors.text.primary}
                            strokeWidth={1.8}
                          />
                        </IconButton>
                      ) : null}
                      {pharmacy.website ? (
                        <IconButton
                          accessibilityLabel={t("access.pharmacy.open_website")}
                          action="access.pharmacy.open_website"
                          onPress={openWebsite}
                          hitSlop={6}
                          size={36}
                          surface="frosted"
                        >
                          <Globe
                            size={16}
                            color={semanticColors.text.primary}
                            strokeWidth={1.8}
                          />
                        </IconButton>
                      ) : null}
                    </View>
                  ) : null}
                </View>
                {contactLine ? <MetaText>{contactLine}</MetaText> : null}
              </View>

              <View style={{ gap: 10 }}>
                <SectionHeading
                  title={t("access.pharmacy.services_section_title")}
                  icon={ListChecks}
                />
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                >
                  {pharmacy.compoundingOnSite ? (
                    <Tag
                      shape="pill"
                      label={t("access.pharmacy.in_house_compounding")}
                    />
                  ) : null}
                  {pharmacy.specialties
                    .filter((specialty) => specialty !== "fertility")
                    .map((specialty) => (
                      <Tag
                        key={specialty}
                        shape="pill"
                        label={formatSpecialty(specialty)}
                      />
                    ))}
                </View>
              </View>

              {source === "demo" && pharmacy.demo ? (
                <View style={{ gap: 10 }}>
                  <SectionHeading
                    title={t("access.pharmacy.demo_quote_label")}
                    icon={Receipt}
                  />
                  <Row
                    title={t("access.pharmacy.demo_quote_medication")}
                    subtitle={t("access.pharmacy.demo_detail_quote_body")}
                    trailing={
                      <PrimaryTitleText style={{ fontWeight: "700" }}>
                        {pharmacy.demo.totalPrice == null
                          ? t("access.pharmacy.contact_for_pricing")
                          : formatPrice(pharmacy.demo.totalPrice)}
                      </PrimaryTitleText>
                    }
                  />
                </View>
              ) : null}

              {source === "demo" && pharmacy.demo?.medicationPrices ? (
                <View style={{ gap: 10 }}>
                  <SectionHeading
                    title={t("access.pharmacy.medication_prices_section_title")}
                    icon={Pill}
                    body={t("access.pharmacy.medication_prices_section_body")}
                  />
                  <View style={{ gap: 8 }}>
                    {medicationPriceRows.map(
                      ({ currentPrice, lowerElsewhere, medication }) => (
                        <MedicationRow
                          key={medication.id}
                          name={medication.name}
                          subtitle={[
                            medication.dosage,
                            lowerElsewhere
                              ? t(
                                  "access.pharmacy.medication_price_lower_elsewhere",
                                  {
                                    params: {
                                      amount: (
                                        (currentPrice ?? 0) -
                                        (lowerElsewhere.price ?? 0)
                                      ).toLocaleString("en-US"),
                                      pharmacy: lowerElsewhere.pharmacy.name,
                                    },
                                  },
                                )
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                          form={medicationFormFromName(medication.name)}
                          value={
                            currentPrice == null
                              ? t("access.pharmacy.medication_price_contact")
                              : formatPrice(currentPrice)
                          }
                        />
                      ),
                    )}
                  </View>
                  {lowerPriceCount > 0 ? (
                    <ActionBanner
                      variant="encouragement"
                      title={t("access.pharmacy.medication_price_split_title")}
                      message={t(
                        "access.pharmacy.medication_price_split_body",
                        { params: { count: String(lowerPriceCount) } },
                      )}
                    />
                  ) : null}
                  <DisclaimerNote>
                    {t("access.pharmacy.medication_price_demo_disclaimer")}
                  </DisclaimerNote>
                </View>
              ) : null}

              {source !== "demo" ? (
                <View style={{ gap: 10 }}>
                  <SectionHeading
                    title={t("access.pharmacy.delivery_section_title")}
                    icon={Truck}
                  />
                  {deliveryFacts.length > 0 ? (
                    deliveryFacts.map((fact) => (
                      <FactLine
                        key={fact.label}
                        icon={fact.icon}
                        label={fact.label}
                      />
                    ))
                  ) : (
                    <FactLine
                      icon={Truck}
                      label={t("access.pharmacy.delivery_not_listed")}
                    />
                  )}
                </View>
              ) : null}

              {source !== "demo" &&
              pharmacy.publishedPrices &&
              pharmacy.publishedPrices.items.length > 0 &&
              matchedMedications.length === 0 ? (
                <View style={{ gap: 12 }}>
                  <SectionHeading
                    title={t("access.pharmacy.published_prices_section_title")}
                    icon={Receipt}
                    body={t("access.pharmacy.published_prices_body", {
                      params: {
                        date: parseDateOrTimestamp(
                          pharmacy.publishedPrices.verifiedOn,
                        ).toLocaleDateString(locale, { dateStyle: "long" }),
                      },
                    })}
                    action={viewAllPrices}
                  />
                  <View style={{ gap: 8 }}>
                    {pharmacy.publishedPrices.items.map((item) => (
                      <MedicationRow
                        key={item.medication}
                        name={item.medication}
                        subtitle=""
                        form={medicationFormFromName(item.medication)}
                        value={formatPrice(item.price)}
                      />
                    ))}
                  </View>
                </View>
              ) : null}

              {source !== "demo" &&
              pharmacy.publishedPrices &&
              matchedMedications.length > 0 ? (
                <View style={{ gap: 12 }}>
                  <SectionHeading
                    icon={Pill}
                    title={t("access.pharmacy.prescription_estimate_title", {
                      params: { pharmacy: pharmacy.name },
                    })}
                    body={t("access.pharmacy.prescription_estimate_body", {
                      params: { pharmacy: pharmacy.name },
                    })}
                    action={viewAllPrices}
                  />
                  <View style={{ gap: 8 }}>
                    {matchedMedications.map((m) => (
                      <MedicationRow
                        key={m.key}
                        name={m.label}
                        subtitle={m.matchedPrice!.medication}
                        form={medicationFormFromName(
                          m.matchedPrice!.medication,
                        )}
                        value={formatPrice(m.matchedPrice!.price)}
                      />
                    ))}
                  </View>
                  <ActionBanner
                    variant="info"
                    title={t(
                      "access.pharmacy.prescription_estimate_starting_from",
                      { params: { amount: formatPrice(publishedPriceTotal) } },
                    )}
                    message={t(
                      "access.pharmacy.prescription_estimate_disclaimer",
                    )}
                  />
                </View>
              ) : null}

              {pharmacy.quoteUrl || pharmacy.sourceUrl || pharmacy.website ? (
                <PrimaryButton
                  label={t(
                    pharmacy.quoteType === "insurance"
                      ? "access.pharmacy.open_insurance_source"
                      : "access.pharmacy.open_quote_source",
                  )}
                  onPress={openQuoteSource}
                  showArrow
                  hapticStyle={null}
                />
              ) : null}
            </CardSurface>
          )}
        </ScrollView>
      </GlassScreenLayout>
    </Animated.View>
  );
}
